/**
 * Form builder — answer types on the left, questions edited in place.
 * ===================================================================
 * 2026-09-29 (Docs/mockups/form-builder-5-options.html, option 4 — the
 * owner's pick). Replaces the Page → Section → Question editor:
 *   - left: a tile per answer type (plus Heading and Page break); a click
 *     adds that kind of question under the selected one;
 *   - middle: the questions as one numbered list; the selected one opens in
 *     place to edit (text, options, Required, "only show if", type, delete).
 * A page is a "Page 2" line in the list, a section is a heading line.
 *
 * The saved shape is still the same pages → sections → fields tree
 * (preChatFormStructure.ts), so the public link, the widget, the app and
 * WhatsApp Flows are unchanged.
 *
 * The Name question is no longer forced or locked: a question whose id is
 * `name` / `full_name` still fills the contact's name (backend identity
 * rules), and a form without one works too.
 */
import React, { useMemo, useState } from 'react';
import {
  BadgeCheck,
  Calendar,
  CalendarClock,
  ChevronDown,
  ChevronUp,
  CircleDot,
  Clock,
  Copy,
  FileUp,
  GitBranch,
  GripVertical,
  Hash,
  Heading,
  Info,
  Mail,
  Phone,
  Pilcrow,
  Plus,
  SeparatorHorizontal,
  SquareCheck,
  Star,
  Trash2,
  Type,
  X,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../components/ui/use-toast';
import {
  FIELD_TYPES,
  FormField,
  FormPage,
  ShowIf,
  newLocalId,
  pageBodySectionId,
  slugifyFieldId,
  waFlag,
} from './preChatFormStructure';

// ─── The flat model ───────────────────────────────────────────────────────────

export type BuilderItem =
  | { kind: 'question'; key: string; field: FormField }
  | { kind: 'heading'; key: string; id: string; title: string; show_if: ShowIf | null }
  | { kind: 'page'; key: string; id: string; title: string };

export interface BuilderState {
  firstPage: { id: string; title?: string };
  items: BuilderItem[];
}

export const OPTION_TYPES = new Set(['select', 'select_one', 'select_many', 'ranking']);
const NUMBER_TYPES = new Set(['number', 'decimal']);
const CONDITION_SOURCE_TYPES = new Set(['select', 'select_one', 'select_many', 'checkbox', 'acknowledge']);
// Question ids that fill the contact's name (backend general_form_defaults identity.name_field_ids).
const CONTACT_NAME_IDS = new Set(['name', 'full_name', 'your_name']);

const TYPE_ICONS: Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = {
  text: Type,
  textarea: Pilcrow,
  email: Mail,
  tel: Phone,
  number: Hash,
  select: CircleDot,
  checkbox: SquareCheck,
  date: Calendar,
  time: Clock,
  datetime: CalendarClock,
  file: FileUp,
  rating: Star,
  acknowledge: BadgeCheck,
  note: Info,
};

// Shorter names for the tiles; the full names (FIELD_TYPES) show on the question.
const TILE_LABELS: Record<string, string> = {
  select: 'Choice',
  acknowledge: 'Agree',
  note: 'Note',
  file: 'File',
  rating: 'Rating',
};

const SELECT_CLS =
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

export function builderFromPages(pages: FormPage[]): BuilderState {
  const items: BuilderItem[] = [];
  (pages || []).forEach((page, pIdx) => {
    if (pIdx > 0) {
      items.push({ kind: 'page', key: newLocalId('item'), id: page.id, title: page.title || '' });
    }
    (page.sections || []).forEach((section) => {
      if (!section.page_body) {
        items.push({
          kind: 'heading',
          key: newLocalId('item'),
          id: section.id,
          title: section.title || '',
          show_if: section.show_if || null,
        });
      }
      (section.fields || []).forEach((field) => {
        items.push({ kind: 'question', key: newLocalId('item'), field: { ...field } });
      });
    });
  });
  const first = pages?.[0];
  return { firstPage: { id: first?.id || 'page_1', title: first?.title }, items };
}

/** Back to the stored pages → sections → fields tree. A page break with
 *  nothing after it is dropped. */
export function pagesFromBuilder(state: BuilderState): FormPage[] {
  const pages: FormPage[] = [{ id: state.firstPage.id, title: state.firstPage.title, sections: [] }];
  let page = pages[0];
  let section: FormPage['sections'][number] | null = null;
  for (const item of state.items) {
    if (item.kind === 'page') {
      page = { id: item.id, title: item.title, sections: [] };
      pages.push(page);
      section = null;
    } else if (item.kind === 'heading') {
      section = { id: item.id, title: item.title, fields: [], show_if: item.show_if || null };
      page.sections.push(section);
    } else {
      if (!section) {
        section = { id: pageBodySectionId(page.id), title: '', fields: [], show_if: null, page_body: true };
        page.sections.push(section);
      }
      section.fields.push(item.field);
    }
  }
  const kept = pages.filter((p, i) => i === 0 || p.sections.length > 0);
  return kept.map((p, i) => ({ ...p, title: (p.title || '').trim() || `Page ${i + 1}` }));
}

/** Problems that block saving, with the item to show. */
export function builderProblems(state: BuilderState): { key: string; message: string } | null {
  for (const item of state.items) {
    if (item.kind !== 'question') continue;
    const f = item.field;
    if (!f.label.trim()) return { key: item.key, message: 'A question has no text' };
    if (OPTION_TYPES.has(f.type) && !(f.options || []).some((o) => o.trim())) {
      return { key: item.key, message: `“${f.label}” needs at least one option` };
    }
  }
  return null;
}

function uniqueFieldId(label: string, items: BuilderItem[], exceptKey?: string): string {
  const taken = new Set(
    items.filter((i) => i.kind === 'question' && i.key !== exceptKey).map((i) => (i as any).field.id as string),
  );
  const base = slugifyFieldId(label || 'question');
  let id = base;
  let n = 2;
  while (taken.has(id)) {
    id = `${base}_${n}`;
    n += 1;
  }
  return id;
}

/** Questions earlier in the list that a condition can depend on. */
function conditionSources(items: BuilderItem[], beforeKey: string): FormField[] {
  const out: FormField[] = [];
  for (const item of items) {
    if (item.key === beforeKey) break;
    if (item.kind === 'question' && CONDITION_SOURCE_TYPES.has(item.field.type)) out.push(item.field);
  }
  return out;
}

function defaultEquals(source?: FormField): string {
  if (!source) return '';
  if (source.type === 'checkbox' || source.type === 'acknowledge') return 'true';
  return source.options?.[0] || '';
}

function typeLabel(type: string): string {
  return FIELD_TYPES.find((t) => t.value === type)?.label || type;
}

/** Bulk options from a JSON array (strings or {title|label|...}) or a newline / comma list. */
export function parseBulkOptionsInput(raw: string): { options: string[]; error?: string } {
  const text = raw.trim();
  if (!text) return { options: [], error: 'Paste a JSON array or a list of options first' };
  const normalizeOne = (item: unknown): string | null => {
    if (typeof item === 'string') return item.trim() || null;
    if (typeof item === 'number' || typeof item === 'boolean') return String(item);
    if (item && typeof item === 'object') {
      const o = item as Record<string, unknown>;
      for (const key of ['title', 'label', 'text', 'value', 'id', 'name']) {
        const v = o[key];
        if (typeof v === 'string' && v.trim()) return v.trim();
        if (typeof v === 'number' || typeof v === 'boolean') return String(v);
      }
    }
    return null;
  };
  let list: unknown[];
  if (text.startsWith('[') || text.startsWith('{')) {
    try {
      const parsed = JSON.parse(text);
      const arr = Array.isArray(parsed) ? parsed : (parsed as { options?: unknown }).options;
      if (!Array.isArray(arr)) {
        return { options: [], error: 'JSON must be an array like ["A","B"] or {"options":["A","B"]}' };
      }
      list = arr;
    } catch {
      return { options: [], error: 'Invalid JSON — use ["Option A", "Option B"]' };
    }
  } else {
    list = (text.includes('\n') ? text.split(/\r?\n/) : text.split(',')).map((s) =>
      s.trim().replace(/^["']|["']$/g, ''),
    );
  }
  const options: string[] = [];
  for (const item of list) {
    const s = normalizeOne(item);
    if (s && !options.includes(s)) options.push(s);
  }
  return options.length ? { options } : { options: [], error: 'No options found in the pasted text' };
}

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  state: BuilderState;
  onChange: (next: BuilderState) => void;
  /** Ids of questions already saved on the form: they keep their id when renamed
   *  (answers and contact attributes are stored under it). */
  savedFieldIds: Set<string>;
  selectedKey: string | null;
  onSelect: (key: string | null) => void;
  title: string;
  subtitle: string;
  /** The form's brand colour: the header stripe and the type icons. */
  color: string;
}

const PreChatFormBuilder: React.FC<Props> = ({
  state,
  onChange,
  savedFieldIds,
  selectedKey,
  onSelect,
  title,
  subtitle,
  color,
}) => {
  const items = state.items;
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [newOption, setNewOption] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [justAdded, setJustAdded] = useState<string | null>(null);

  const setItems = (next: BuilderItem[]) => onChange({ ...state, items: next });

  const selectItem = (key: string | null) => {
    onSelect(key);
    setNewOption('');
    setBulkOpen(false);
    setJustAdded(null);
  };

  // Question numbers, in list order.
  const numbers = useMemo(() => {
    const out: Record<string, number> = {};
    let n = 0;
    for (const item of items) {
      if (item.kind === 'question') {
        n += 1;
        out[item.field.id] = n;
      }
    }
    return out;
  }, [items]);

  const selectedIndex = items.findIndex((i) => i.key === selectedKey);

  // ── Changes ──

  const insertAfterSelection = (item: BuilderItem) => {
    const at = selectedIndex >= 0 ? selectedIndex + 1 : items.length;
    setItems([...items.slice(0, at), item, ...items.slice(at)]);
    onSelect(item.key);
    setJustAdded(item.key);
    setBulkOpen(false);
    setNewOption('');
  };

  const addQuestion = (type: string) =>
    insertAfterSelection({
      kind: 'question',
      key: newLocalId('item'),
      field: { id: uniqueFieldId('question', items), type, label: '', required: false, options: [] },
    });

  const addHeading = () =>
    insertAfterSelection({ kind: 'heading', key: newLocalId('item'), id: newLocalId('section'), title: '', show_if: null });

  const addPageBreak = () =>
    insertAfterSelection({ kind: 'page', key: newLocalId('item'), id: newLocalId('page'), title: '' });

  const duplicateQuestion = (key: string) => {
    const item = items.find((i) => i.key === key);
    if (!item || item.kind !== 'question') return;
    const copy: BuilderItem = {
      kind: 'question',
      key: newLocalId('item'),
      field: { ...item.field, id: uniqueFieldId(item.field.label || 'question', items), options: [...(item.field.options || [])] },
    };
    insertAfterSelection(copy);
  };

  const replaceItem = (key: string, next: BuilderItem, rest: BuilderItem[] = items) =>
    rest.map((i) => (i.key === key ? next : i));

  const updateField = (key: string, patch: Partial<FormField>) => {
    const item = items.find((i) => i.key === key);
    if (!item || item.kind !== 'question') return;
    let field = { ...item.field, ...patch };
    let next = items;
    // A question that isn't saved yet takes its id from its text, so answers
    // are stored under a readable key ("phone_number", not "question_3").
    if (patch.label !== undefined && !savedFieldIds.has(item.field.id)) {
      const newId = uniqueFieldId(patch.label || 'question', items, key);
      if (newId !== item.field.id) {
        const oldId = item.field.id;
        field = { ...field, id: newId };
        next = items.map((i) => {
          if (i.kind === 'question' && i.field.show_if?.field_id === oldId) {
            return { ...i, field: { ...i.field, show_if: { ...i.field.show_if, field_id: newId } } };
          }
          if (i.kind === 'heading' && i.show_if?.field_id === oldId) {
            return { ...i, show_if: { ...i.show_if, field_id: newId } };
          }
          return i;
        });
      }
    }
    setItems(replaceItem(key, { ...item, field }, next));
  };

  const updateItem = (key: string, patch: Record<string, any>) => {
    const item = items.find((i) => i.key === key);
    if (!item) return;
    setItems(replaceItem(key, { ...item, ...patch } as BuilderItem));
  };

  const removeItem = (key: string) => {
    const item = items.find((i) => i.key === key);
    if (!item) return;
    let next = items.filter((i) => i.key !== key);
    if (item.kind === 'question') {
      // Conditions that depended on it go too.
      let cleared = 0;
      next = next.map((i) => {
        if (i.kind === 'question' && i.field.show_if?.field_id === item.field.id) {
          cleared += 1;
          return { ...i, field: { ...i.field, show_if: null } };
        }
        if (i.kind === 'heading' && i.show_if?.field_id === item.field.id) {
          cleared += 1;
          return { ...i, show_if: null };
        }
        return i;
      });
      if (cleared) {
        toast({ title: `Removed ${cleared} condition${cleared > 1 ? 's' : ''} that depended on this question` });
      }
    }
    setItems(next);
    const idx = items.indexOf(item);
    selectItem(next[Math.min(idx, next.length - 1)]?.key || null);
  };

  const moveItem = (from: number, to: number) => {
    if (from < 0 || to < 0 || from >= items.length || to >= items.length || from === to) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setItems(next);
  };

  // ── Small pieces ──

  const dragProps = (item: BuilderItem, index: number) => ({
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      setDragKey(item.key);
      e.dataTransfer.effectAllowed = 'move';
    },
    onDragOver: (e: React.DragEvent) => {
      if (dragKey) e.preventDefault();
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      if (dragKey) moveItem(items.findIndex((i) => i.key === dragKey), index);
      setDragKey(null);
    },
    onDragEnd: () => setDragKey(null),
  });

  // The opened card takes drops but isn't dragged itself (so selecting text
  // in its boxes doesn't move it); it moves with the arrows.
  const dropProps = (index: number) => ({
    onDragOver: (e: React.DragEvent) => {
      if (dragKey) e.preventDefault();
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      if (dragKey) moveItem(items.findIndex((i) => i.key === dragKey), index);
      setDragKey(null);
    },
  });

  const grip = <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-muted-foreground/60" aria-hidden />;

  const arrows = (index: number) => (
    <span className="flex shrink-0 items-center">
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="h-8 w-8 text-muted-foreground"
        aria-label="Move up"
        disabled={index === 0}
        onClick={() => moveItem(index, index - 1)}
      >
        <ChevronUp className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="h-8 w-8 text-muted-foreground"
        aria-label="Move down"
        disabled={index === items.length - 1}
        onClick={() => moveItem(index, index + 1)}
      >
        <ChevronDown className="h-4 w-4" />
      </Button>
    </span>
  );

  const deleteButton = (key: string, label: string) => (
    <Button
      type="button"
      size="icon"
      variant="ghost"
      aria-label={label}
      title={label}
      className="h-8 w-8 text-muted-foreground hover:text-destructive"
      onClick={() => removeItem(key)}
    >
      <Trash2 className="h-4 w-4" />
    </Button>
  );

  const conditionChip = (show_if: ShowIf | null | undefined) => {
    if (!show_if?.field_id) return null;
    const n = numbers[show_if.field_id];
    const value = show_if.equals === 'true' ? 'ticked' : show_if.equals;
    return (
      <Badge variant="outline" className="shrink-0 gap-1 border-amber-600/50 text-[11px] font-medium text-amber-500">
        <GitBranch className="h-3 w-3" />
        {n ? `If ${n} is ${value}` : 'Condition'}
      </Badge>
    );
  };

  const conditionLink = (itemKey: string, set: (v: ShowIf | null) => void, what: string) => {
    const sources = conditionSources(items, itemKey);
    return (
      <button
        type="button"
        className="flex items-center gap-1.5 text-sm font-medium text-primary hover:underline disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
        disabled={sources.length === 0}
        title={sources.length === 0 ? 'Add a choice, tick box or agree question above it first' : undefined}
        onClick={() => set({ field_id: sources[0].id, equals: defaultEquals(sources[0]) })}
      >
        <GitBranch className="h-4 w-4" /> Only show {what} if…
      </button>
    );
  };

  const conditionEditor = (itemKey: string, show_if: ShowIf, set: (v: ShowIf | null) => void, what: string) => {
    const sources = conditionSources(items, itemKey);
    const source = sources.find((s) => s.id === show_if.field_id);
    const tickType = source?.type === 'checkbox' || source?.type === 'acknowledge';
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-600/40 bg-amber-500/5 px-3 py-2.5">
        <GitBranch className="h-4 w-4 shrink-0 text-amber-500" />
        <span className="text-sm text-amber-500">Show {what} only if</span>
        <select
          className={cn(SELECT_CLS, 'h-8 w-auto max-w-[260px]')}
          aria-label="Depends on question"
          value={show_if.field_id}
          onChange={(e) => {
            const s = sources.find((x) => x.id === e.target.value);
            set({ field_id: e.target.value, equals: defaultEquals(s) });
          }}
        >
          {!source && <option value={show_if.field_id}>(question no longer above)</option>}
          {sources.map((s) => (
            <option key={s.id} value={s.id}>
              {numbers[s.id]}. {s.label || 'Untitled question'}
            </option>
          ))}
        </select>
        <span className="text-sm text-muted-foreground">is</span>
        {tickType ? (
          <span className="text-sm font-medium">ticked</span>
        ) : (
          <select
            className={cn(SELECT_CLS, 'h-8 w-auto')}
            aria-label="Answer"
            value={show_if.equals}
            onChange={(e) => set({ field_id: show_if.field_id, equals: e.target.value })}
          >
            {(source?.options || []).map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        )}
        <button
          type="button"
          className="ml-auto text-xs text-muted-foreground hover:text-foreground"
          onClick={() => set(null)}
        >
          Remove
        </button>
      </div>
    );
  };

  // ── Collapsed rows ──

  const collapsedRow = (item: BuilderItem, index: number) => {
    if (item.kind === 'page') {
      const pageNo = items.slice(0, index + 1).filter((i) => i.kind === 'page').length + 1;
      return (
        <button
          key={item.key}
          type="button"
          {...dragProps(item, index)}
          onClick={() => selectItem(item.key)}
          className={cn('my-1 flex w-full items-center gap-3 rounded-md px-1 py-1 hover:bg-muted/30', dragKey === item.key && 'opacity-50')}
        >
          {grip}
          <span className="h-px flex-1 border-t border-dashed border-border" />
          <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
            Page {pageNo}
            {item.title ? ` · ${item.title}` : ''}
          </span>
          <span className="h-px flex-1 border-t border-dashed border-border" />
        </button>
      );
    }
    if (item.kind === 'heading') {
      return (
        <button
          key={item.key}
          type="button"
          {...dragProps(item, index)}
          onClick={() => selectItem(item.key)}
          className={cn(
            'mt-3 flex w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left hover:bg-muted/30',
            dragKey === item.key && 'opacity-50',
          )}
        >
          {grip}
          <span className={cn('flex-1 truncate text-base font-semibold', !item.title && 'italic text-muted-foreground')}>
            {item.title || 'Untitled heading'}
          </span>
          {conditionChip(item.show_if)}
        </button>
      );
    }
    const f = item.field;
    return (
      <button
        key={item.key}
        type="button"
        {...dragProps(item, index)}
        onClick={() => selectItem(item.key)}
        className={cn(
          'flex w-full items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-left transition-colors hover:border-muted-foreground/40',
          dragKey === item.key && 'opacity-50',
        )}
      >
        {grip}
        <span className="w-5 shrink-0 text-xs font-semibold text-muted-foreground">{numbers[f.id]}</span>
        <span className={cn('flex-1 truncate text-sm font-semibold', !f.label && 'italic text-muted-foreground')}>
          {f.label || 'Untitled question'}
          {f.required && <span className="ml-0.5 text-destructive">*</span>}
        </span>
        {conditionChip(f.show_if)}
        {waFlag(f.type) !== 'ok' && (
          <Badge variant="outline" className="shrink-0 border-amber-700 text-[10px] text-amber-500">
            WA {waFlag(f.type)}
          </Badge>
        )}
        <span className="shrink-0 text-xs text-muted-foreground">{typeLabel(f.type)}</span>
      </button>
    );
  };

  // ── The selected item, opened in place ──

  const cardCls = 'rounded-xl border border-border bg-card shadow-[0_0_0_2px_hsl(var(--ring)/0.35)]';

  const openItem = (item: BuilderItem, index: number) => {
    if (item.kind === 'page') {
      const pageNo = items.slice(0, index + 1).filter((i) => i.kind === 'page').length + 1;
      return (
        <div key={item.key} {...dropProps(index)} className={cn(cardCls, 'my-1 flex items-center gap-3 px-4 py-2.5')}>
          <SeparatorHorizontal className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="shrink-0 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Page {pageNo}</span>
          <Input
            aria-label="Page name"
            value={item.title}
            autoFocus={justAdded === item.key}
            placeholder="Name (only for you — visitors just press Next)"
            onChange={(e) => updateItem(item.key, { title: e.target.value })}
            className="h-8"
          />
          {arrows(index)}
          {deleteButton(item.key, 'Remove page break')}
        </div>
      );
    }

    if (item.kind === 'heading') {
      return (
        <div key={item.key} {...dropProps(index)} className={cn(cardCls, 'mt-3 space-y-3 p-4')}>
          <div className="flex items-center gap-3">
              <Heading className="h-4 w-4 shrink-0 text-muted-foreground" />
            <Input
              aria-label="Heading text"
              value={item.title}
              autoFocus={justAdded === item.key}
              placeholder="Heading, e.g. About your visit"
              onChange={(e) => updateItem(item.key, { title: e.target.value })}
              className="h-10 text-base font-semibold"
            />
          </div>
          {item.show_if?.field_id &&
            conditionEditor(item.key, item.show_if, (v) => updateItem(item.key, { show_if: v }), 'this group')}
          <div className="flex items-center gap-3 border-t border-border pt-3">
            <span className="text-xs text-muted-foreground">Groups the questions below it</span>
            {!item.show_if?.field_id && conditionLink(item.key, (v) => updateItem(item.key, { show_if: v }), 'this group')}
            <div className="flex-1" />
            {arrows(index)}
            {deleteButton(item.key, 'Remove heading')}
          </div>
        </div>
      );
    }

    const f = item.field;
    const hasOptions = OPTION_TYPES.has(f.type);
    const flag = waFlag(f.type);
    const TypeIcon = TYPE_ICONS[f.type] || Type;
    const addOption = () => {
      const text = newOption.trim();
      if (!text) return;
      const current = f.options || [];
      if (text.startsWith('[') || text.startsWith('{')) {
        const { options, error } = parseBulkOptionsInput(text);
        if (error) {
          toast({ title: error, variant: 'destructive' });
          return;
        }
        updateField(item.key, { options: [...current, ...options.filter((o) => !current.includes(o))] });
      } else if (current.includes(text)) {
        toast({ title: 'That option already exists', variant: 'destructive' });
        return;
      } else {
        updateField(item.key, { options: [...current, text] });
      }
      setNewOption('');
    };

    return (
      <div key={item.key} {...dropProps(index)} className={cn(cardCls, 'space-y-3 p-4')}>
        <div className="flex items-center gap-3">
          <span className="w-5 shrink-0 text-sm font-bold" style={{ color }}>
            {numbers[f.id]}
          </span>
          {f.type === 'note' ? (
            <Textarea
              aria-label="Text to show"
              autoFocus={justAdded === item.key}
              value={f.label}
              rows={2}
              placeholder="Text to show on the form"
              onChange={(e) => updateField(item.key, { label: e.target.value })}
            />
          ) : (
            <Input
              aria-label="Question"
              autoFocus={justAdded === item.key}
              value={f.label}
              placeholder="Type your question"
              onChange={(e) => updateField(item.key, { label: e.target.value })}
              className="h-10 text-[15px] font-semibold"
            />
          )}
        </div>

        {hasOptions && (
          <div className="space-y-2 pl-8">
            <div className="flex flex-wrap items-center gap-1.5">
              {(f.options || []).map((opt, oIdx) => (
                <span
                  key={oIdx}
                  className="inline-flex items-center rounded-full border border-border bg-muted/40 py-0.5 pl-3 pr-1"
                >
                  <input
                    aria-label={`Option ${oIdx + 1}`}
                    value={opt}
                    size={Math.max(opt.length, 3)}
                    onChange={(e) => {
                      const next = [...(f.options || [])];
                      next[oIdx] = e.target.value;
                      updateField(item.key, { options: next });
                    }}
                    className="bg-transparent text-sm font-medium outline-none"
                  />
                  <button
                    type="button"
                    aria-label={`Remove option ${opt}`}
                    className="ml-1 rounded-full p-1 text-muted-foreground hover:text-destructive"
                    onClick={() => updateField(item.key, { options: (f.options || []).filter((_, i) => i !== oIdx) })}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <span className="inline-flex items-center gap-1 rounded-full border border-dashed border-border py-0.5 pl-2.5 pr-2">
                <Plus className="h-3.5 w-3.5 text-muted-foreground" />
                <input
                  aria-label="New option"
                  value={newOption}
                  placeholder="Add option"
                  onChange={(e) => setNewOption(e.target.value)}
                  onBlur={addOption}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addOption();
                    }
                  }}
                  className="w-28 bg-transparent py-1 text-sm outline-none placeholder:text-muted-foreground"
                />
              </span>
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground"
                onClick={() => setBulkOpen((v) => !v)}
              >
                {bulkOpen ? 'Hide' : 'Paste many'}
              </button>
            </div>
            {bulkOpen && (
              <div className="flex items-start gap-2">
                <Textarea
                  value={bulkText}
                  rows={3}
                  className="font-mono text-xs"
                  placeholder={'One per line, or ["A", "B", "C"]'}
                  onChange={(e) => setBulkText(e.target.value)}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={!bulkText.trim()}
                  onClick={() => {
                    const { options, error } = parseBulkOptionsInput(bulkText);
                    if (error) {
                      toast({ title: error, variant: 'destructive' });
                      return;
                    }
                    const merged = [...(f.options || [])];
                    options.forEach((o) => !merged.includes(o) && merged.push(o));
                    updateField(item.key, { options: merged });
                    setBulkText('');
                    setBulkOpen(false);
                  }}
                >
                  Add
                </Button>
              </div>
            )}
          </div>
        )}

        {NUMBER_TYPES.has(f.type) && (
          <div className="flex items-center gap-2 pl-8 text-sm text-muted-foreground">
            <span>Between</span>
            {(['min', 'max'] as const).map((k, i) => (
              <React.Fragment key={k}>
                {i === 1 && <span>and</span>}
                <Input
                  type="number"
                  aria-label={k === 'min' ? 'Lowest allowed' : 'Highest allowed'}
                  placeholder="any"
                  value={f.validation?.[k] ?? ''}
                  onChange={(e) => {
                    const v = { ...(f.validation || {}) };
                    if (e.target.value === '') delete v[k];
                    else v[k] = Number(e.target.value);
                    updateField(item.key, { validation: Object.keys(v).length ? v : undefined });
                  }}
                  className="h-8 w-24"
                />
              </React.Fragment>
            ))}
          </div>
        )}

        {f.show_if?.field_id &&
          conditionEditor(item.key, f.show_if, (v) => updateField(item.key, { show_if: v }), 'this question')}

        <div className="flex flex-wrap items-center gap-3 border-t border-border pt-3">
          <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <TypeIcon className="h-4 w-4" style={{ color }} />
            <select
              aria-label="Answer type"
              value={f.type}
              onChange={(e) =>
                updateField(item.key, {
                  type: e.target.value,
                  options: OPTION_TYPES.has(e.target.value) ? f.options || [] : [],
                  validation: NUMBER_TYPES.has(e.target.value) ? f.validation : undefined,
                })
              }
              className="cursor-pointer rounded-md bg-transparent py-1 pr-1 text-xs font-medium text-foreground outline-none hover:bg-muted/40"
            >
              {FIELD_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          {!f.show_if?.field_id &&
            conditionLink(item.key, (v) => updateField(item.key, { show_if: v }), 'this question')}
          <div className="flex-1" />
          {f.type !== 'note' && (
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              Required
              <Switch checked={!!f.required} onCheckedChange={(v) => updateField(item.key, { required: v })} />
            </label>
          )}
          <span className="h-5 w-px bg-border" />
          {arrows(index)}
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label="Duplicate question"
            title="Duplicate"
            className="h-8 w-8 text-muted-foreground"
            onClick={() => duplicateQuestion(item.key)}
          >
            <Copy className="h-4 w-4" />
          </Button>
          {deleteButton(item.key, 'Delete question')}
        </div>

        <p className="pl-8 text-[11px] text-muted-foreground">
          {flag === 'unsupported'
            ? 'Not available on WhatsApp — skipped there. '
            : flag === 'limited'
              ? 'On WhatsApp this is asked as a 1–5 choice. '
              : ''}
          Answer saved as <code className="rounded bg-muted px-1 py-0.5">{f.id}</code>
          {CONTACT_NAME_IDS.has(f.id) ? ' — fills the contact’s name.' : ''}
        </p>
      </div>
    );
  };

  // ── Layout ──

  const tiles: { key: string; label: string; Icon: React.ComponentType<any>; onClick: () => void }[] = [
    ...FIELD_TYPES.map((t) => ({
      key: t.value,
      label: TILE_LABELS[t.value] || t.label,
      Icon: TYPE_ICONS[t.value] || Type,
      onClick: () => addQuestion(t.value),
    })),
    { key: 'heading', label: 'Heading', Icon: Heading, onClick: addHeading },
    { key: 'page', label: 'Page break', Icon: SeparatorHorizontal, onClick: addPageBreak },
  ];

  return (
    <div className="flex h-full min-h-0">
      <aside className="w-[250px] shrink-0 overflow-y-auto border-r border-border px-4 py-5">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Add</p>
        <div className="grid grid-cols-2 gap-1.5">
          {tiles.map(({ key, label, Icon, onClick }) => (
            <button
              key={key}
              type="button"
              onClick={onClick}
              className="flex flex-col items-start gap-2 rounded-lg border border-border bg-card p-2.5 text-left text-xs font-semibold transition-colors hover:border-muted-foreground/50 hover:bg-muted/30"
            >
              <Icon className="h-4 w-4" style={{ color }} />
              {label}
            </button>
          ))}
        </div>
        <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
          Click to add {selectedIndex >= 0 ? 'under the selected question' : 'at the end'}.
        </p>
      </aside>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
        <div className="mx-auto flex max-w-2xl flex-col gap-2">
          <div className="mb-2 rounded-xl border border-border bg-card px-5 py-4" style={{ borderTop: `3px solid ${color}` }}>
            <p className="text-lg font-bold tracking-tight">{title || 'Untitled form'}</p>
            {subtitle && <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>}
          </div>

          {items.length === 0 && (
            <div className="rounded-xl border border-dashed border-border px-6 py-12 text-center text-sm text-muted-foreground">
              Pick an answer type on the left to add your first question.
            </div>
          )}

          {items.map((item, index) => (item.key === selectedKey ? openItem(item, index) : collapsedRow(item, index)))}
        </div>
      </div>
    </div>
  );
};

export default PreChatFormBuilder;
